import { memo, useMemo, useEffect, useRef, useCallback } from 'react';
import { parseReference, type BibleBook, BIBLE_BOOK_CODES } from '@/lib/utils/bible-utils';
import { useManuscript } from '@/context/manuscript-context';
import { StickyNote, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';

/* 
 * BIBLE PARAGRAPH COMPONENT
 * 
 * This component is responsible for:
 * 1. Parsing and rendering a paragraph of Bible text.
 * 2. Handling verse registration for the Intersection Observer (visible verse tracking).
 * 3. Integrating with the Manuscript Marking system.
 * 4. Optimizing rendering performance by isolating word-level re-renders.
 * 
 * PERFORMANCE ARCHITECTURE:
 * - Context is consumed at the BibleParagraph level.
 * - Manuscript data is accessed via O(1) Maps (marksByWord, dividersByWord).
 * - RenderWord is a pure 'memo' component that only re-renders when its specific data changes.
 * - This prevents the entire chapter from re-rendering when a single word is selected.
 */

// Debug logging flag
// const DEBUG_PARAGRAPH_REGISTRATION = false;

interface Props {
    paragraph: TranslationParagraph;
    version: string;
    startVerse: number;
    startOffset: number;
    paragraphIndex?: number;
    footnotesByWord?: Map<number, TranslationFootnote>;
    onFootnoteClick?: (footnote: TranslationFootnote, element: HTMLElement) => void;
    registerVerse?: (
        element: Element | null,
        book: BibleBook,
        chapter: number,
        verse: number,
        paragraphIndex: number
    ) => (() => void) | undefined;
}

/**
 * Pre-computed header classes for Title paragraphs.
 * NOTE: Never add margins here. Virtuoso (the scrolling library) relies on 
 * precise height calculations. Margins can cause 'jumping' during scroll.
 * We use padding (pt/pb) instead.
 */
const HEADER_CLASSES = {
    1: 'text-3xl font-bold pt-4 pb-4',
    2: 'text-2xl font-light pt-6 pb-4 tracking-tight',
    3: 'text-lg font-semibold pt-4 pb-2',
    4: 'text-sm italic pt-3 pb-2 opacity-80'
} as const;

/**
 * Renders a single paragraph or title from a Bible translation.
 */
export const BibleParagraph = memo(function BibleParagraph({ paragraph, version, startVerse, startOffset, paragraphIndex, registerVerse, footnotesByWord, onFootnoteClick }: Props) {
    // Consume ManuscriptContext once per paragraph.
    const {
        marksByWord,
        dividersByWord,
        isInSelection,
        addMark,
        setActiveSelection,
        drawModeStyleId
    } = useManuscript();

    // Parse reference (e.g., "Gen 1:1")
    const { book, chapter } = useMemo(() => parseReference(paragraph.r), [paragraph.r]);
    const bookCode = useMemo(() => BIBLE_BOOK_CODES[book], [book]);

    // Extract verse number from paragraph reference
    const verseNumber = useMemo(() => {
        const parts = paragraph.r.split(':');
        if (parts.length === 5) {
            const verseStart = parseFloat(parts[2]);
            return verseStart === 0 ? 1 : Math.ceil(verseStart);
        }
        return 1;
    }, [paragraph.r]);

    // Split text into word segments
    const words = useMemo(() => paragraph.t.split(' ').filter(w => w), [paragraph.t]);

    /**
     * Pre-calculate the verse and word index for every word in this paragraph.
     * Segments are stored as "Text*Features". New verses are encoded with 'v#'.
     */
    const wordContexts = useMemo(() => {
        let verse = startVerse;
        let offset = startOffset;
        return words.map(word => {
            const features = word.includes('*') ? word.substring(word.indexOf('*') + 1) : '';
            const vPos = features.indexOf('v');
            if (vPos !== -1) {
                const vVal = features.slice(vPos + 1);
                if (vVal.includes(':')) {
                    const [_, v] = vVal.split(':');
                    verse = parseInt(v);
                } else {
                    verse = parseInt(vVal);
                }
                offset = 0;
            }
            const currentOffset = offset;
            offset++;
            return { verse, offset: currentOffset };
        });
    }, [words, startVerse, startOffset]);

    /**
     * Stable handler for word clicks.
     * Only active in draw mode for rapid marking workflow.
     * Normal marking uses text selection via ManuscriptInteractionLayer.
     */
    const handleWordClick = useCallback((wordId: string, text: string) => {
        // Only handle clicks in draw mode
        if (!drawModeStyleId) return;

        // Draw mode: Apply the locked style immediately
        addMark({
            markStyleId: drawModeStyleId,
            startWordId: wordId,
            endWordId: wordId,
            text
        });
    }, [drawModeStyleId, addMark]);

    // Render Titles
    if (paragraph.h > 0) {
        const headerClasses = HEADER_CLASSES[paragraph.h as keyof typeof HEADER_CLASSES] || HEADER_CLASSES[4];
        const headerRef = useRef<HTMLHeadingElement>(null);

        useEffect(() => {
            if (!headerRef.current || !registerVerse || !book || !chapter || paragraphIndex === undefined) return;
            return registerVerse(headerRef.current, book, chapter, verseNumber, paragraphIndex);
        }, [registerVerse, book, chapter, verseNumber, paragraphIndex]);

        return (
            <h2 ref={headerRef} className={cn(headerClasses, paragraphIndex === 0 && "!pt-0")}>
                {paragraph.t}
                {paragraph.h === 1 && (
                    <span className="text-base font-normal text-muted-foreground ml-3 align-baseline tracking-normal">
                        ({(version || 'CSB').toUpperCase()})
                    </span>
                )}
            </h2>
        );
    }

    // Render Standard Paragraphs
    return (
        <div className="pt-1 pb-3">
            {words.map((word, index) => {
                const verse = wordContexts[index].verse;
                const offset = wordContexts[index].offset;
                const wordId = `w-${version}-${bookCode}-${chapter}-${verse}-${offset}`;

                // O(1) Lookups from the context Maps
                const wordMarks = marksByWord.get(wordId);
                const divider = dividersByWord.get(wordId);
                const isSelected = isInSelection(wordId);
                const footnote = footnotesByWord?.get(index);

                return (
                    <RenderWord
                        key={`word-${paragraph.r}-${index}`}
                        word={word}
                        wordId={wordId}
                        isFirstWord={index === 0}
                        book={book}
                        chapter={chapter}
                        paragraphIndex={paragraphIndex}
                        registerVerse={registerVerse}
                        isSelected={isSelected}
                        applicableMarks={wordMarks}
                        activeDivider={divider}
                        onClick={handleWordClick}
                        footnote={footnote}
                        onFootnoteClick={onFootnoteClick}
                    />
                );
            })}
        </div>
    );
});

interface RenderWordProps {
    word: string;
    wordId: string;
    isFirstWord?: boolean;
    book?: BibleBook;
    chapter?: number;
    paragraphIndex?: number;
    isSelected: boolean;
    applicableMarks?: ManuscriptMark[];
    activeDivider?: ManuscriptDivider;
    footnote?: TranslationFootnote;
    onFootnoteClick?: (footnote: TranslationFootnote, element: HTMLElement) => void;
    registerVerse?: (
        element: Element | null,
        book: BibleBook,
        chapter: number,
        verse: number,
        paragraphIndex: number
    ) => (() => void) | undefined;
    onClick: (wordId: string, text: string) => void;
}

const VERSE_NUMBER_CLASS = "align-super text-[0.70em] text-muted-foreground mr-0.25 drop-shadow font-thin";

/**
 * Individual Word Component. 
 * Renders the text along with any manuscript indicators (marks, notes, dividers).
 */
const RenderWord = memo(function RenderWord({
    word, wordId, isFirstWord, book, chapter, paragraphIndex, registerVerse, isSelected, applicableMarks, activeDivider, onClick, footnote, onFootnoteClick
}: RenderWordProps) {
    const verseRef = useRef<HTMLElement>(null);
    const footnoteRef = useRef<HTMLElement>(null);
    const [text, features = ''] = word.split('*');
    const { drawModeStyleId, showAnnotations, transientHighlights, markStyles } = useManuscript();

    /**
     * Features and CSS are memoized locally.
     */
    const { verseNumber, cssClasses, lineBreak } = useMemo(() => {
        const { verseNumber, featureSet } = parseWordFeatures(features);
        return buildCssClasses(featureSet, isFirstWord || false, verseNumber);
    }, [features, isFirstWord]);

    /**
     * Register verse for scrolling observer.
     */
    useEffect(() => {
        if (!verseNumber || !verseRef.current || !registerVerse || !book || !chapter || paragraphIndex === undefined) return;
        return registerVerse(verseRef.current, book, chapter, verseNumber, paragraphIndex);
    }, [verseNumber, registerVerse, book, chapter, paragraphIndex]);

    const hasNote = applicableMarks?.some(m => !!m.annotation && m.endWordId === wordId);

    /**
     * Calculate visual manuscript styles.
     */
    const { markClasses, markStyleProperties } = useMemo(() => {
        if (!applicableMarks || applicableMarks.length === 0) return { markClasses: '', markStyleProperties: {} };

        // Sort by update date descending to prioritize the "freshest" mark
        const sortedMarks = [...applicableMarks].sort((a, b) => (b.dateUpdated || 0) - (a.dateUpdated || 0));

        // Find the freshest mark that actually has a visual style assigned
        const freshestStyledMark = sortedMarks.find(m => !!m.markStyleId);

        const classSet = new Set<string>();
        // Always include the base mark class if there's a styled mark
        if (freshestStyledMark) classSet.add('mark');

        const styleProps: any = {};

        // Pushing classes for all marks to support custom style IDs, but deduplicated
        applicableMarks.forEach(m => {
            if (m.markStyleId) classSet.add(`mark-${m.markStyleId}`);
        });

        let isStart = false;
        let isEnd = false;
        let primaryColor = '';
        let primaryTexture = '';

        if (freshestStyledMark) {
            const style = markStyles.find(s => s.id === freshestStyledMark.markStyleId);

            if (style) {
                primaryColor = style.color;
                if (style.texture) primaryTexture = style.texture;

                // Add the type-specific class (e.g., mark-box, mark-underline, mark-circle)
                classSet.add(`mark-${style.type}`);

                // Identify boundaries for rounded corners, etc.
                if (freshestStyledMark.startWordId === wordId) isStart = true;
                if (freshestStyledMark.endWordId === wordId) isEnd = true;

                // Add thickness property
                if (style.thickness) {
                    styleProps['--mark-thickness'] = `${style.thickness}px`;
                }
            }
        }

        if (primaryTexture) {
            classSet.add(`mark-texture-${primaryTexture}`);
        } else if (freshestStyledMark) {
            classSet.add('mark-texture-marker');
        }

        if (isStart) classSet.add('mark-is-start');
        if (isEnd) classSet.add('mark-is-end');

        // Create dynamic styles for the mark color
        if (primaryColor) {
            const freshestStyle = freshestStyledMark ? markStyles.find(s => s.id === freshestStyledMark.markStyleId) : null;
            const isHighlight = freshestStyle?.type === 'highlight';
            let color = primaryColor;
            if (isHighlight && color.startsWith('rgb(') && !color.includes('rgba')) {
                color = color.replace('rgb', 'rgba').replace(')', ', 0.35)');
            }
            styleProps['--mark-color'] = color;
        }

        return {
            markClasses: Array.from(classSet).join(' '),
            markStyleProperties: styleProps
        };
    }, [applicableMarks, wordId, markStyles]);

    // Only show pointer cursor in draw mode, otherwise allow natural text selection
    const cursorClass = drawModeStyleId ? 'cursor-pointer' : 'cursor-text';
    const isTransientHighlighted = transientHighlights.has(wordId);
    const ghostClass = isTransientHighlighted ? 'bg-amber-400/40 shadow-[0_0_12px_rgba(251,191,36,0.6)] animate-pulse rounded-sm z-20 relative' : '';

    return (
        <>
            {lineBreak && <br />}
            <span
                id={wordId}
                className={`${cssClasses} ${cursorClass} ${ghostClass} hover:bg-muted/30 transition-colors relative select-text ${isSelected ? 'bg-primary/20 ring-1 ring-primary/30 rounded-sm' : ''} ${showAnnotations ? `${markClasses} ${activeDivider ? 'word-with-divider' : ''}` : ''}`}
                style={markStyleProperties}
                onClick={() => onClick(wordId, text)}
            >
                {showAnnotations && activeDivider && (
                    <span className={`divider-content divider-${activeDivider.type}`}>
                        {activeDivider.type === 'slash' ? '/' : '//'}
                    </span>
                )}
                {verseNumber && (
                    <sup ref={verseRef} className={VERSE_NUMBER_CLASS}>{verseNumber}</sup>
                )}
                <span className="relative z-10">{text}</span>
                {footnote && (
                    <sup
                        ref={footnoteRef}
                        className="text-[0.65em] text-primary cursor-pointer hover:text-primary/80 transition-colors ml-0.5 inline-flex items-baseline -translate-y-1"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onFootnoteClick && footnoteRef.current) {
                                onFootnoteClick(footnote, footnoteRef.current);
                            }
                        }}
                    >
                        <MessageSquare className="h-2 w-2 mb-[1px] fill-primary/10" strokeWidth={2.5} />
                    </sup>
                )}
                <span> </span>
                {hasNote && showAnnotations && (
                    <StickyNote className="absolute -top-2 -right-1 h-3.5 w-3.5 text-amber-600 fill-amber-100 drop-shadow-sm" strokeWidth={2.5} />
                )}
            </span>
        </>
    );
});

/**
 * PARSE WORD FEATURES
 * 
 * Features are encoded in the translation data following a '*'.
 * Supported Features:
 * - b: Bold
 * - i: Italic
 * - s: Small-caps
 * - n: New line with 1.5rem indent
 * - d: Double line with 3.0rem indent
 * - l: Line break (no indent)
 * - c: Line break (Centered)
 * - g: Line break (Right aligned)
 * - e: Line break (Left aligned)
 * - v#: Verse number marker (starts verse #) (always last, e.g. v6)
 */
function parseWordFeatures(features: string) {
    let verseNumber: number | undefined;
    const featureSet = new Set<string>();
    let i = 0;
    while (i < features.length) {
        if (features[i] === 'v') {
            verseNumber = parseInt(features.slice(i + 1));
            break;
        }
        featureSet.add(features[i]);
        i++;
    }
    return { verseNumber, featureSet };
}

/**
 * BUILD CSS CLASSES
 * 
 * Translates the internal feature set into Tailwind CSS utility classes.
 */
function buildCssClasses(featureSet: Set<string>, isFirstWord: boolean, verseNumber: number | undefined) {
    const classes = [];
    let lineBreak = false;

    // Character Styles
    if (featureSet.has('b')) classes.push('font-bold');
    if (featureSet.has('i')) classes.push('italic');
    if (featureSet.has('s')) classes.push('text-sm [font-variant-caps:small-caps]');

    // Layout Styles (Only applied if not the start of a paragraph)
    if (!isFirstWord) {
        if (featureSet.has('n')) { lineBreak = true; classes.push('pl-[1.5rem]'); }
        else if (featureSet.has('d')) { lineBreak = true; classes.push('pl-[3rem]'); }
        else if (featureSet.has('l')) { lineBreak = true; }
        else if (featureSet.has('c')) { lineBreak = true; classes.push('text-center'); }
        else if (featureSet.has('g')) { lineBreak = true; classes.push('text-right'); }
        else if (featureSet.has('e')) { lineBreak = true; classes.push('text-left'); }
    }

    return { verseNumber, cssClasses: classes.join(' '), lineBreak };
}